import React from 'react';
import { styled } from '@mui/system';
// import Image from '../../components/Image';
import { Typography, Box } from '@mui/material';
import RightIcon from '@mui/icons-material/ChevronRight';
import { Link } from 'react-router-dom';
import TroubleshootOutlinedIcon from '@mui/icons-material/TroubleshootOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';
import { isActionAllowed } from '../../utils/allowedActions';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
import { userLogin } from '@Services/hasAdminAccess';

const NavigationBtnContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: '24px',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: '16px',
  },
}));

const NavigationBtn = styled(Link, {
  shouldForwardProp: prop => prop !== 'disabled',
})<{ disabled?: boolean }>(({ theme, disabled }) => ({
  textDecoration: 'none',
  color: 'inherit',
  height: '120px',
  padding: '24px',
  display: 'grid',
  gridTemplateColumns: '48px 1fr 24px',
  alignItems: 'center',
  gap: '16px',
  border: '1px solid',
  borderRadius: '12px',
  borderColor: disabled ? theme.palette.action.disabled : theme.palette.divider,
  backgroundColor: disabled ? theme.palette.action.hover : theme.palette.background.paper,
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'all 0.2s ease-in-out',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  opacity: disabled ? 0.7 : 1,
  '&:hover': !disabled && {
    backgroundColor: theme.palette.primary.light,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-2px)',
    borderColor: theme.palette.primary.main,
  },
  '&:active': !disabled && {
    transform: 'translateY(0)',
  },
}));

const IconContainer = styled('div')<{ disabled?: boolean }>(({ theme, disabled }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: disabled ? theme.palette.action.disabledBackground : theme.palette.primary.light,
  color: disabled ? theme.palette.action.disabled : theme.palette.primary.main,
}));

function Navigation() {
  // const theme = useTheme();
  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);
  const isUserLogin = userLogin();

  const isGetRatesAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Shipment_Details.get_rates,
    PERFORM_ACTION.write,
  );

  const isCreateBulkShipmentsAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Shipment_Details.create_bulk_shipments,
    PERFORM_ACTION.write,
  );

  const isCreateSingleShipmentAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Shipment_Details.create_single_shipment,
    PERFORM_ACTION.write,
  );

  const navigationItems = [
    {
      title: 'Get Rates',
      path: '/get-rates',
      icon: <TroubleshootOutlinedIcon sx={{ fontSize: 24 }} />,
      allowed: !isUserLogin ? isGetRatesAllowed : true,
      description: 'Compare shipping rates',
    },
    {
      title: 'Place a Single Shipment',
      path: '/shipment/create-single',
      icon: <LocalShippingIcon sx={{ fontSize: 24 }} />,
      allowed: !isUserLogin ? isCreateSingleShipmentAllowed : true,
      description: 'Create individual shipment',
    },
    {
      title: 'Place a Bulk Shipment',
      path: '/shipment/create-bulk',
      icon: <InventoryIcon sx={{ fontSize: 24 }} />,
      allowed: !isUserLogin ? isCreateBulkShipmentsAllowed : true,
      description: 'Create multiple shipments',
    },
  ];

  return (
    <NavigationBtnContainer>
      {navigationItems.map((item, index) => (
        <NavigationBtn
          key={index}
          to={item.allowed ? item.path : ''}
          disabled={!item.allowed}
          onClick={e => !item.allowed && e.preventDefault()}
        >
          <IconContainer disabled={!item.allowed}>{item.icon}</IconContainer>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '16px',
                color: !item.allowed ? 'text.disabled' : 'text.primary',
                mb: 0.5,
              }}
            >
              {item.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: !item.allowed ? 'text.disabled' : 'text.secondary',
                fontSize: '14px',
              }}
            >
              {item.description}
            </Typography>
          </Box>
          <RightIcon
            sx={{
              fontSize: 20,
              color: !item.allowed ? 'action.disabled' : 'primary.main',
              justifySelf: 'flex-end',
              transition: 'transform 0.2s',
              '.MuiLink-root:hover &': !item.allowed
                ? {}
                : {
                    transform: 'translateX(4px)',
                  },
            }}
          />
        </NavigationBtn>
      ))}
    </NavigationBtnContainer>
  );
}

export default Navigation;
